/**
 * Navigation store mirroring React Navigation (navigate/replace/goBack). A
 * Zustand store so non-component code (e.g. socketStore) can navigate too.
 *
 * Web is history-authoritative: each entry carries a `__depth` marker in
 * history.state; goBack pops when depth > 0, else falls back so we don't eject
 * from the SPA. Native keeps an in-store `stack`.
 */

import { create } from "zustand";
import { Platform } from "react-native";
import {
  type Route,
  type RouteName,
  type RouteParams,
  routeFromPath,
  pathFromRoute,
} from "./routes";

const isWeb = Platform.OS === "web";

/** Where goBack() lands when there is no in-app entry to pop. */
const FALLBACK_ROUTE: RouteName = "landing";

function webDepth(): number {
  return (window.history.state && (window.history.state as any).__depth) || 0;
}

function initialRoute(): Route {
  if (isWeb) {
    if (!window.history.state || (window.history.state as any).__depth == null) {
      window.history.replaceState({ __depth: 0 }, "", window.location.pathname + window.location.search);
    }
    return routeFromPath(window.location.pathname + window.location.search);
  }
  return { name: "landing", params: {} };
}

interface NavigationState {
  route: Route;
  /** Native-only back stack of prior routes (most recent last). Unused on web. */
  stack: Route[];
  navigate: <N extends RouteName>(name: N, params?: RouteParams[N]) => void;
  replace: <N extends RouteName>(name: N, params?: RouteParams[N]) => void;
  /** Enter the builder with the back stack reset to [landing], so back always returns to landing. */
  resetToBuilder: (params?: RouteParams["builder"]) => void;
  goBack: () => void;
  /** Apply a route parsed from the current URL (web popstate) without pushing history. */
  syncFromPath: (fullPath: string) => void;
  /** True if goBack() will stay within the app. */
  canGoBack: () => boolean;
}

const sameRoute = (a: Route, b: Route): boolean =>
  a.name === b.name && pathFromRoute(a) === pathFromRoute(b);

export const useNavigationStore = create<NavigationState>((set, get) => ({
  route: initialRoute(),
  stack: [],

  navigate: (name, params) => {
    const next: Route = { name, params: (params ?? {}) as RouteParams[typeof name] };
    const current = get().route;
    if (sameRoute(current, next)) {
      // Same destination — replace so we don't stack a duplicate history entry.
      get().replace(name, params);
      return;
    }
    if (isWeb) {
      window.history.pushState({ __depth: webDepth() + 1 }, "", pathFromRoute(next));
      set({ route: next });
    } else {
      set({ route: next, stack: [...get().stack, current] });
    }
  },

  replace: (name, params) => {
    const next: Route = { name, params: (params ?? {}) as RouteParams[typeof name] };
    if (isWeb) {
      // Preserve depth — replace swaps the entry, it doesn't add one.
      window.history.replaceState({ __depth: webDepth() }, "", pathFromRoute(next));
    }
    set({ route: next });
  },

  resetToBuilder: (params) => {
    const builder: Route = { name: "builder", params: (params ?? {}) as RouteParams["builder"] };
    const landing: Route = { name: "landing", params: {} };
    if (isWeb) {
      // Collapse to [landing, builder] at depths 0 and 1 so back from builder reaches landing.
      window.history.replaceState({ __depth: 0 }, "", pathFromRoute(landing));
      window.history.pushState({ __depth: 1 }, "", pathFromRoute(builder));
      set({ route: builder });
    } else {
      set({ route: builder, stack: [landing] });
    }
  },

  goBack: () => {
    if (isWeb) {
      if (webDepth() > 0) {
        window.history.back(); // popstate reconciles the route
      } else {
        get().replace(FALLBACK_ROUTE); // on the opened-on entry — don't leave the SPA
      }
      return;
    }
    const stack = get().stack;
    if (stack.length === 0) {
      set({ route: { name: FALLBACK_ROUTE, params: {} } });
      return;
    }
    const prev = stack[stack.length - 1];
    set({ route: prev, stack: stack.slice(0, -1) });
  },

  syncFromPath: (fullPath) => {
    // Anchor entries lacking __depth at 0 so depth reads stay deterministic.
    if (isWeb && (window.history.state == null || (window.history.state as any).__depth == null)) {
      window.history.replaceState({ __depth: 0 }, "", fullPath);
    }
    set({ route: routeFromPath(fullPath) });
  },

  canGoBack: () => {
    if (isWeb) return webDepth() > 0;
    return get().stack.length > 0;
  },
}));
